import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import eventService from '../services/eventService';
import { API_V1 } from '../services/api';
import { COMMENT_REQUIRED_MAX_RATING, SENTIMENTS, SENTIMENT_COLORS } from './FeedbackWidget';

/**
 * Bewertung als Hinweis unten in der Mitte (Startseite, seit 27.10.2026). Ersetzt den Kasten in der
 * Seitenleiste, den es neben der Karte nicht mehr gibt.
 *
 * Er kommt **25 Sekunden nach dem Ankommen auf der Website**, auf jeder öffentlichen Seite
 * (seit 30.10.2026 in `App.tsx` statt nur auf der Startseite, ohne Bedingung an Klicks oder
 * Scrollen: die Messung über Scrollen schlug fehl, weil `body` scrollt). Danach ruht er: nach dem
 * Senden ein halbes Jahr (182 Tage, Wunsch Phil 30.10.2026), nach dem Schließen 21 Tage.
 *
 * Ein Klick reicht (29.10.2026): die Raute gilt als abgegeben, die Rauten weichen einem Textfeld
 * mit Ablaufbalken. Wer hineinklickt oder schreibt, hält es an; sonst geht die Bewertung ohne Text
 * raus und der Hinweis verschwindet. Abgeschickt wird erst dann, sonst stünde dieselbe Bewertung
 * zweimal in der Liste. Bis 3 Rauten verlangt der Server einen Text, dort läuft kein Balken.
 * Das Feld für die E-Mail-Adresse ist entfallen: bis Oktober 2026 hatten 6 von 63 Bewertungen eine.
 */
const SPEICHER = 'feedback-hinweis';
const RUHE_GESENDET = 182 * 86400000;
const RUHE_GESCHLOSSEN = 21 * 86400000;
const MIN_ZEIT_MS = 25000;
const SITZUNG_BEGINN = 'feedback-hinweis-beginn';
/** So lange wartet das Textfeld nach einer guten Bewertung, bevor es von selbst geht. */
export const NACHTRAG_MS = 10000;

function ruht(): boolean {
  try {
    const v = JSON.parse(localStorage.getItem(SPEICHER) || 'null') as { am: number; gesendet: boolean } | null;
    if (!v) return false;
    return Date.now() - v.am < (v.gesendet ? RUHE_GESENDET : RUHE_GESCHLOSSEN);
  } catch { return false; }
}
function merken(gesendet: boolean) {
  try { localStorage.setItem(SPEICHER, JSON.stringify({ am: Date.now(), gesendet })); } catch { /* privat */ }
}

type Props = {
  className?: string;
  /** Ohne Wartezeit und Gedächtnis zeigen (Design-Lab). */
  sofort?: boolean;
  /** Design-Lab: mit dieser Bewertung beginnen, ohne Ablauf und ohne zu senden. */
  vorschauRating?: number;
};

const FeedbackToast: React.FC<Props> = ({ className, sofort = false, vorschauRating }) => {
  const vorschau = vorschauRating != null;
  const [sichtbar, setSichtbar] = useState(sofort || vorschau);
  const [rating, setRating] = useState(vorschauRating ?? 0);
  const [hover, setHover] = useState(0);
  const [text, setText] = useState('');
  const [gehalten, setGehalten] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [gesendet, setGesendet] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Eine gute Bewertung, die noch nicht beim Server ist. Geht beim Verlassen der Seite mit raus.
  const offen = useRef<number | null>(null);
  const textJetzt = useRef('');
  textJetzt.current = text.trim();

  // Nach 25 Sekunden auf der Website, egal auf welcher Seite (Wunsch Phil, 30.10.2026). Der
  // Beginn steht in der Sitzung, damit Seitenwechsel und Neuladen die Uhr nicht zurückstellen.
  useEffect(() => {
    if (sofort || vorschau || ruht()) return;
    let beginn = Date.now();
    try {
      const gemerkt = Number(sessionStorage.getItem(SITZUNG_BEGINN));
      if (gemerkt > 0) beginn = gemerkt; else sessionStorage.setItem(SITZUNG_BEGINN, String(beginn));
    } catch { /* privat */ }
    const t = window.setTimeout(() => { if (!ruht()) setSichtbar(true); }, Math.max(0, MIN_ZEIT_MS - (Date.now() - beginn)));
    return () => window.clearTimeout(t);
  }, [sofort, vorschau]);

  const senden = useCallback(async (r: number, kommentar?: string) => {
    offen.current = null;
    merken(true);
    try {
      await eventService.submitFeedback({ rating: r, comment: kommentar, platform: 'web' });
    } catch { /* Danke zeigen wir trotzdem, niemand soll hier festhängen. */ }
  }, []);

  // Tab zu oder anderswohin navigiert, bevor abgeschickt ist: die Bewertung geht mit dem bisherigen Text raus.
  useEffect(() => {
    const raus = () => {
      const r = offen.current;
      if (r == null) return;
      offen.current = null;
      merken(true);
      fetch(`${API_V1}/feedback`, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating: r, comment: textJetzt.current || undefined, platform: 'web' }) }).catch(() => {});
    };
    window.addEventListener('pagehide', raus);
    return () => { window.removeEventListener('pagehide', raus); raus(); };
  }, []);

  // Der Ablauf: ohne Text und ohne Klick ins Feld geht die Bewertung nach NACHTRAG_MS von selbst.
  // Nur, solange sie noch offen ist: nach „Schließen“ bleibt der Hinweis eingehängt (er gibt nur
  // null zurück), der Ablauf liefe weiter und schickte dieselbe Bewertung ein zweites Mal
  // (so geschehen vom 30. auf den 31.10.2026, fünf doppelte Einträge).
  const laeuft = rating > COMMENT_REQUIRED_MAX_RATING && !gehalten && !gesendet;
  useEffect(() => {
    if (!laeuft || vorschau) return;
    const t = window.setTimeout(() => {
      if (offen.current != null) senden(offen.current);
      setSichtbar(false);
    }, NACHTRAG_MS);
    return () => window.clearTimeout(t);
  }, [laeuft, vorschau, rating, senden]);

  // Nach dem Dank von selbst wieder weg.
  useEffect(() => {
    if (!gesendet || vorschau) return;
    const t = window.setTimeout(() => setSichtbar(false), 3000);
    return () => window.clearTimeout(t);
  }, [gesendet, vorschau]);

  if (!sichtbar) return null;

  const aktiv = hover || rating;
  const textPflicht = rating > 0 && rating <= COMMENT_REQUIRED_MAX_RATING;

  const bewerten = (i: number) => {
    setRating(i);
    if (i > COMMENT_REQUIRED_MAX_RATING && !vorschau) offen.current = i;
  };
  // Schließen nach einer guten Bewertung schickt sie ab; ohne Bewertung oder bei fehlendem Pflichttext nichts.
  const schliessen = () => {
    if (offen.current != null) senden(offen.current, textJetzt.current || undefined);
    else merken(false);
    setSichtbar(false);
  };
  const abschicken = async () => {
    if (!text.trim() || sendet) return;
    setSendet(true);
    if (!vorschau) await senden(rating, text.trim());
    setSendet(false);
    setGesendet(true);
  };

  return (
    <div ref={ref} className={`fb-hinweis${className ? ` ${className}` : ''}`} role="dialog" aria-label="Wie gefällt dir Radsport Events?">
      {gesendet ? (
        <div className="fb-dank">
          <span className="fb-dank-icon" aria-hidden><Check size={18} /></span>
          <span><strong>Danke!</strong> Wir lesen jede Rückmeldung.</span>
        </div>
      ) : rating === 0 ? (
        <>
          <div className="fb-kopf">
            <strong>Wie gefällt dir Radsport Events?</strong>
            <button type="button" className="fb-zu" onClick={schliessen} aria-label="Schließen"><X size={18} aria-hidden /></button>
          </div>
          <div className="fb-rauten" role="radiogroup" aria-label="Bewertung" onMouseLeave={() => setHover(0)}>
            {[1, 2, 3, 4, 5].map(i => (
              <button key={i} type="button" role="radio" aria-checked={false} aria-label={`${i} von 5: ${SENTIMENTS[i]}`}
                className={i <= aktiv ? 'fb-raute fb-raute--an' : 'fb-raute'}
                onClick={() => bewerten(i)} onMouseEnter={() => setHover(i)}>
                <span aria-hidden />
              </button>
            ))}
            <span className="fb-stimmung" style={{ color: aktiv ? SENTIMENT_COLORS[aktiv] : '#6A6A6A' }}>{aktiv ? SENTIMENTS[aktiv] : 'Tippe zum Bewerten'}</span>
          </div>
        </>
      ) : (
        <div className="fb-nachtrag ui-aufklappen">
          <div className="fb-kopf">
            {textPflicht
              ? <strong>Was hat dir nicht gefallen?</strong>
              : <span className="fb-danke-kurz"><span className="fb-dank-icon fb-dank-icon--klein" aria-hidden><Check size={14} /></span><strong>Danke!</strong></span>}
            <button type="button" className="fb-zu" onClick={schliessen} aria-label="Schließen"><X size={18} aria-hidden /></button>
          </div>
          {laeuft && <div className="fb-ablauf" style={vorschau
            ? { animationDuration: `${NACHTRAG_MS}ms`, animationDelay: `-${NACHTRAG_MS * 0.4}ms`, animationPlayState: 'paused' }
            : { animationDuration: `${NACHTRAG_MS}ms` }} aria-hidden />}
          <textarea value={text} onChange={e => { setText(e.target.value); setGehalten(true); }} onFocus={() => setGehalten(true)}
            maxLength={2000} rows={2} autoFocus={textPflicht}
            placeholder={textPflicht ? 'Ein Satz hilft uns schon.' : 'Was können wir noch besser machen?'}
            aria-label={textPflicht ? 'Was hat dir nicht gefallen?' : 'Kommentar zur Bewertung'} className="fb-feld" />
          {(textPflicht || text.trim() !== '') && (
            <div className="fb-fuss">
              <span className="fb-anonym">Anonym</span>
              <button type="button" className="fb-senden" onClick={abschicken} disabled={sendet || !text.trim()}>{sendet ? 'Senden …' : 'Senden'}</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedbackToast;
